import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../lib/useTranslation';
import { useUnsavedChanges } from '../../lib/useUnsavedChanges';
import {
  createVehicle, deleteVehicle, getVehicleCatalogue, getCostCurrencyPreference, listFuelCountries, listVehicles,
  saveCostCurrencyPreference, updateVehicle,
} from '../../../core/costs.js';
import { textValue, vehicleLabel, type CostVehicle } from './costModel';
import './CostSettings.css';

interface Props {
  canWrite: boolean;
  canApprove: boolean;
  openAddOnMount?: boolean;
  captureOnly?: boolean; onCreated?: (vehicle: CostVehicle) => void; onCancel?: () => void;
  onWorkStateChange?: (state: { busy: boolean; dirty: boolean }) => void;
}

interface Country {
  code: string;
  name?: string;
  currency?: string;
  gasoline?: number | null;
  diesel?: number | null;
}

interface CatalogueItem {
  value: string;
  text: string;
}

interface VehicleForm {
  id: string;
  mode: 'catalogue' | 'manual';
  name: string;
  licensePlate: string;
  year: string;
  make: string;
  model: string;
  details: string;
  energyType: string;
  consumptionPer100Km: string;
  odometerUnit: string;
  isDefault: boolean;
  fuelCountryCode: string;
  electricityPricePerKwh: string;
  electricityPriceCurrency: string;
  electricityPriceCountry: string;
  catalogueSource: string;
  catalogueSourceId: string;
  catalogueSourceUrl: string;
}

const EMPTY_FORM: VehicleForm = {
  id: '', mode: 'catalogue', name: '', licensePlate: '', year: '', make: '', model: '', details: '',
  energyType: 'GASOLINE', consumptionPer100Km: '', odometerUnit: 'KM', isDefault: false,
  fuelCountryCode: 'CH', electricityPricePerKwh: '', electricityPriceCurrency: 'CHF',
  electricityPriceCountry: '', catalogueSource: '', catalogueSourceId: '', catalogueSourceUrl: '',
};

function fromVehicle(vehicle: CostVehicle): VehicleForm {
  return {
    ...EMPTY_FORM,
    id: vehicle.id,
    mode: textValue(vehicle.catalogueSource) ? 'catalogue' : 'manual',
    name: textValue(vehicle.name),
    licensePlate: textValue(vehicle.licensePlate),
    year: textValue(vehicle.year),
    make: textValue(vehicle.make),
    model: textValue(vehicle.model),
    details: textValue(vehicle.details),
    energyType: textValue(vehicle.energyType).toUpperCase() || 'GASOLINE',
    consumptionPer100Km: textValue(vehicle.consumptionPer100Km),
    odometerUnit: textValue(vehicle.odometerUnit).toUpperCase() || 'KM',
    isDefault: Boolean(vehicle.isDefault),
    fuelCountryCode: textValue(vehicle.fuelCountryCode) || 'CH',
    electricityPricePerKwh: textValue(vehicle.electricityPricePerKwh),
    electricityPriceCurrency: textValue(vehicle.electricityPriceCurrency) || 'CHF',
    electricityPriceCountry: textValue(vehicle.electricityPriceCountry),
    catalogueSource: textValue(vehicle.catalogueSource),
    catalogueSourceId: textValue(vehicle.catalogueSourceId),
    catalogueSourceUrl: textValue(vehicle.catalogueSourceUrl),
  };
}

export default function CostSettings({ canWrite, canApprove, openAddOnMount = false, captureOnly = false, onCreated, onCancel, onWorkStateChange }: Props) {
  const t = useTranslation();
  const [vehicles, setVehicles] = useState<CostVehicle[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<VehicleForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [years, setYears] = useState<CatalogueItem[]>([]);
  const [makes, setMakes] = useState<CatalogueItem[]>([]);
  const [models, setModels] = useState<CatalogueItem[]>([]);
  const [options, setOptions] = useState<CatalogueItem[]>([]);
  const [selection, setSelection] = useState({ year: '', make: '', model: '', vehicleId: '' });
  const [catalogueBusy, setCatalogueBusy] = useState(false);
  const [catalogueError, setCatalogueError] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('CHF');
  const [savedDisplayCurrency, setSavedDisplayCurrency] = useState('CHF');
  const [currencyOptions, setCurrencyOptions] = useState<string[]>(['CHF']);
  const [currencyBusy, setCurrencyBusy] = useState(false);
  const [currencyError, setCurrencyError] = useState('');
  const [currencySaved, setCurrencySaved] = useState(false);
  const initialAddOpened = useRef(false);
  const vehicleBaseline = useRef('');
  const leaveVehicle = useUnsavedChanges(!!form && JSON.stringify({ form, selection }) !== vehicleBaseline.current, saving || catalogueBusy);
  useUnsavedChanges(displayCurrency !== savedDisplayCurrency, currencyBusy);

  const workCallback = useRef(onWorkStateChange); workCallback.current = onWorkStateChange;
  const vehicleDirty = !!form && JSON.stringify({ form, selection }) !== vehicleBaseline.current;
  useEffect(() => { workCallback.current?.({ busy: saving || catalogueBusy, dirty: vehicleDirty }); }, [saving, catalogueBusy, vehicleDirty]);
  useEffect(() => () => workCallback.current?.({ busy: false, dirty: false }), []);
  const closeEditor = () => { if (leaveVehicle()) { setForm(null); onCancel?.(); } };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [vehicleResult, countryResult, yearResult, currencyResult] = await Promise.allSettled([
      listVehicles(), listFuelCountries(), getVehicleCatalogue('years'), getCostCurrencyPreference(),
    ]);
    if (vehicleResult.status === 'fulfilled') setVehicles((vehicleResult.value as { vehicles?: CostVehicle[] }).vehicles || []);
    else setError(vehicleResult.reason instanceof Error ? vehicleResult.reason.message : t('settings.costs.loadFailed'));
    if (countryResult.status === 'fulfilled') setCountries((countryResult.value as { countries?: Country[] }).countries || []);
    if (yearResult.status === 'fulfilled') setYears((yearResult.value as { items?: CatalogueItem[] }).items || []);
    else setCatalogueError(t('settings.costs.catalogueUnavailable'));
    if (currencyResult.status === 'fulfilled') {
      const preference = (currencyResult.value as { preference?: { currency?: string; currencies?: string[] } }).preference;
      const currency = preference?.currency || 'CHF';
      setDisplayCurrency(currency);
      setSavedDisplayCurrency(currency);
      setCurrencySaved(false);
      setCurrencyOptions(preference?.currencies?.length ? preference.currencies : [currency]);
    } else setCurrencyError(t('settings.costs.displayCurrencyLoadFailed'));
    setLoading(false);
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const selectedCountry = useMemo(() => countries.find((country) => country.code === form?.fuelCountryCode), [countries, form?.fuelCountryCode]);

  const edit = (vehicle: CostVehicle) => {
    if (!leaveVehicle()) return;
    vehicleBaseline.current = JSON.stringify({ form: fromVehicle(vehicle), selection: { year: textValue(vehicle.year), make: textValue(vehicle.make), model: textValue(vehicle.model), vehicleId: textValue(vehicle.catalogueSourceId) } });
    setForm(fromVehicle(vehicle));
    setFormError('');
    setSelection({ year: textValue(vehicle.year), make: textValue(vehicle.make), model: textValue(vehicle.model), vehicleId: textValue(vehicle.catalogueSourceId) });
  };

  const add = () => {
    if (!leaveVehicle()) return;
    vehicleBaseline.current = JSON.stringify({ form: { ...EMPTY_FORM, isDefault: vehicles.length === 0 }, selection: { year: '', make: '', model: '', vehicleId: '' } });
    setForm({ ...EMPTY_FORM, isDefault: vehicles.length === 0 });
    setFormError('');
    setSelection({ year: '', make: '', model: '', vehicleId: '' });
    setMakes([]); setModels([]); setOptions([]);
  };

  useEffect(() => {
    if (!loading && canWrite && openAddOnMount && !initialAddOpened.current) {
      initialAddOpened.current = true;
      add();
    }
  }, [canWrite, loading, openAddOnMount]);

  const set = <K extends keyof VehicleForm>(key: K, value: VehicleForm[K]) => {
    setForm((current) => current ? { ...current, [key]: value } : current);
  };

  const loadCatalogue = async (kind: 'makes' | 'models' | 'options' | 'vehicle', params: Record<string, string>) => {
    setCatalogueBusy(true);
    setCatalogueError('');
    try {
      const response = await getVehicleCatalogue(kind, params) as { items?: CatalogueItem[]; vehicle?: Record<string, unknown> };
      if (kind === 'makes') setMakes(response.items || []);
      if (kind === 'models') setModels(response.items || []);
      if (kind === 'options') setOptions(response.items || []);
      if (kind === 'vehicle' && response.vehicle) {
        const vehicle = response.vehicle;
        setForm((current) => current ? {
          ...current,
          name: [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
          year: textValue(vehicle.year, selection.year), make: textValue(vehicle.make, selection.make), model: textValue(vehicle.model, selection.model),
          details: textValue(vehicle.details), energyType: textValue(vehicle.energyType).toUpperCase() || current.energyType,
          consumptionPer100Km: textValue(vehicle.consumptionPer100Km), catalogueSource: textValue(vehicle.catalogueSource),
          catalogueSourceId: textValue(vehicle.sourceId, params.id), catalogueSourceUrl: textValue(vehicle.catalogueSourceUrl),
        } : current);
      }
    } catch (requestError) {
      setCatalogueError(requestError instanceof Error ? requestError.message : t('settings.costs.catalogueUnavailable'));
    } finally {
      setCatalogueBusy(false);
    }
  };

  const chooseYear = (year: string) => {
    setSelection({ year, make: '', model: '', vehicleId: '' }); setMakes([]); setModels([]); setOptions([]);
    if (year) void loadCatalogue('makes', { year });
  };
  const chooseMake = (make: string) => {
    const next = { ...selection, make, model: '', vehicleId: '' }; setSelection(next); setModels([]); setOptions([]);
    if (make) void loadCatalogue('models', { year: next.year, make });
  };
  const chooseModel = (model: string) => {
    const next = { ...selection, model, vehicleId: '' }; setSelection(next); setOptions([]);
    if (model) void loadCatalogue('options', { year: next.year, make: next.make, model });
  };
  const chooseVehicle = (vehicleId: string) => {
    setSelection((current) => ({ ...current, vehicleId }));
    if (vehicleId) void loadCatalogue('vehicle', { id: vehicleId });
  };

  const saveVehicle = async () => {
    if (!form) return;
    const consumption = Number(form.consumptionPer100Km);
    const electricPrice = Number(form.electricityPricePerKwh);
    if (!form.name.trim()) { setFormError(t('settings.costs.vehicleNameRequired')); return; }
    if (!['GASOLINE', 'DIESEL', 'ELECTRIC'].includes(form.energyType) || !Number.isFinite(consumption) || consumption <= 0) {
      setFormError(t('settings.costs.vehicleConsumptionRequired')); return;
    }
    if (form.energyType === 'ELECTRIC' && (!Number.isFinite(electricPrice) || electricPrice <= 0)) {
      setFormError(t('settings.costs.electricityPriceRequired')); return;
    }
    const optional = (key: string, value: string | number | boolean) => value === '' ? {} : { [key]: value };
    const payload = {
      name: form.name.trim(),
      ...optional('licensePlate', form.licensePlate.trim()),
      ...optional('year', form.year ? Number(form.year) : ''),
      ...optional('make', form.make.trim()), ...optional('model', form.model.trim()), ...optional('details', form.details.trim()),
      energyType: form.energyType, consumptionPer100Km: consumption, odometerUnit: form.odometerUnit, isDefault: form.isDefault,
      ...optional('fuelCountryCode', form.fuelCountryCode),
      ...(form.energyType === 'ELECTRIC' ? {
        electricityPricePerKwh: electricPrice,
        electricityPriceCurrency: form.electricityPriceCurrency || selectedCountry?.currency || 'CHF',
        electricityPriceCountry: form.electricityPriceCountry || selectedCountry?.name || form.fuelCountryCode,
      } : {}),
      ...optional('catalogueSource', form.catalogueSource), ...optional('catalogueSourceId', form.catalogueSourceId), ...optional('catalogueSourceUrl', form.catalogueSourceUrl),
    };
    setSaving(true); setFormError('');
    try {
      if (form.id) await updateVehicle(form.id, payload);
      else { const result = await createVehicle(payload); if (onCreated) { setForm(null); onCreated(result.vehicle); return; } }
      setForm(null);
      await load();
    } catch (requestError) {
      setFormError(requestError instanceof Error ? requestError.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const removeVehicle = async (id: string) => {
    setSaving(true); setFormError('');
    try { await deleteVehicle(id); setDeleteId(''); setForm(null); await load(); }
    catch (requestError) { setFormError(requestError instanceof Error ? requestError.message : t('common.deleteFailed')); }
    finally { setSaving(false); }
  };

  const setDefault = async (vehicle: CostVehicle) => {
    try { await updateVehicle(vehicle.id, { isDefault: true }); await load(); }
    catch (requestError) { setError(requestError instanceof Error ? requestError.message : t('common.saveFailed')); }
  };

  const saveDisplayCurrency = async () => {
    setCurrencyBusy(true); setCurrencyError(''); setCurrencySaved(false);
    try {
      const response = await saveCostCurrencyPreference(displayCurrency) as { preference?: { currency?: string } };
      const currency = response.preference?.currency || displayCurrency;
      setDisplayCurrency(currency);
      setSavedDisplayCurrency(currency);
      setCurrencySaved(true);
    } catch (requestError) {
      setCurrencyError(requestError instanceof Error ? requestError.message : t('settings.costs.displayCurrencySaveFailed'));
    } finally {
      setCurrencyBusy(false);
    }
  };

  const currencyChanged = displayCurrency !== savedDisplayCurrency;

  if (loading) return <div className="csg-state"><span className="cst-spinner" />{t('common.loading')}</div>;

  return (
    <div className="csg-root">
      {captureOnly && error && <div className="cst-alert cst-alert-error" role="alert">{error}<button type="button" onClick={() => void load()}>{t('common.retry')}</button></div>}
      {!captureOnly && <>
      <p className="csg-lead">{t('settings.costs.lead')}</p>
      {error && <div className="cst-alert cst-alert-error">{error}</div>}

      <section className="csg-card">
        <header className="csg-card-head"><span className="csg-card-icon material-symbols-rounded">currency_exchange</span><div><h3>{t('settings.costs.displayCurrencyTitle')}</h3><p>{t('settings.costs.displayCurrencyHint')}</p></div></header>
        <div className="csg-currency-row">
          <label className="cst-field"><span>{t('settings.costs.displayCurrencyLabel')}</span><select className="cst-input" value={displayCurrency} disabled={currencyBusy} onChange={(event) => { setDisplayCurrency(event.target.value); setCurrencySaved(false); }}>{currencyOptions.map((currency) => <option value={currency} key={currency}>{currency}</option>)}</select></label>
          {currencyChanged && <button type="button" className="cst-btn-primary" disabled={currencyBusy} onClick={() => void saveDisplayCurrency()}>{currencyBusy && <span className="cst-spinner" />}{currencyBusy ? t('common.saving') : t('common.save')}</button>}
          {currencySaved && <span className="csg-currency-saved"><span className="material-symbols-rounded">check_circle</span>{t('settings.costs.displayCurrencySaved')}</span>}
        </div>
        {currencyError && <div className="cst-alert cst-alert-error csg-currency-error">{currencyError}</div>}
      </section>

      <section className="csg-card">
        <header className="csg-card-head"><span className="csg-card-icon material-symbols-rounded">directions_car</span><div><h3>{t('settings.costs.vehiclesTitle')}</h3><p>{t('settings.costs.vehiclesHint')}</p></div>{canWrite && <button type="button" className="cst-btn-ghost" onClick={add}><span className="material-symbols-rounded">add</span>{t('settings.costs.addVehicle')}</button>}</header>
        {vehicles.length ? <div className="csg-vehicle-list">{vehicles.map((vehicle) => (
          <div className="csg-vehicle-row" key={vehicle.id}>
            <span className="csg-vehicle-icon material-symbols-rounded">{textValue(vehicle.energyType).toUpperCase() === 'ELECTRIC' ? 'electric_car' : 'directions_car'}</span>
            <div><strong>{vehicleLabel(vehicle)}</strong><span>{[vehicle.licensePlate, t('settings.costs.energy.' + textValue(vehicle.energyType).toLowerCase()), vehicle.consumptionPer100Km ? t('settings.costs.consumptionValue', { value: vehicle.consumptionPer100Km }) : ''].filter(Boolean).join(' · ')}</span></div>
            {vehicle.isDefault ? <span className="csg-default"><span className="material-symbols-rounded">check_circle</span>{t('settings.costs.defaultVehicle')}</span> : canWrite ? <button type="button" className="csg-text-btn" onClick={() => void setDefault(vehicle)}>{t('settings.costs.makeDefault')}</button> : null}
            {canWrite && <button type="button" className="cst-icon-btn" onClick={() => edit(vehicle)} aria-label={t('common.edit')}><span className="material-symbols-rounded">edit</span></button>}
          </div>
        ))}</div> : <div className="csg-empty"><span className="material-symbols-rounded">no_crash</span><strong>{t('settings.costs.noVehicles')}</strong><p>{t('settings.costs.noVehiclesHint')}</p></div>}
      </section>

      </>}
      {form && canWrite && (
        <section className="csg-card csg-editor">
          <header className="csg-editor-head"><h3>{t(form.id ? 'settings.costs.editVehicle' : 'settings.costs.addVehicle')}</h3><button type="button" className="cst-icon-btn" disabled={saving || catalogueBusy} onClick={closeEditor} aria-label={t('common.close')}><span className="material-symbols-rounded">close</span></button></header>
          <div className="csg-mode"><button type="button" className={form.mode === 'catalogue' ? 'active' : ''} onClick={() => set('mode', 'catalogue')}>{t('settings.costs.catalogueMode')}</button><button type="button" className={form.mode === 'manual' ? 'active' : ''} onClick={() => set('mode', 'manual')}>{t('settings.costs.manualMode')}</button></div>

          {form.mode === 'catalogue' && <div className="csg-catalogue">
            <div className="csg-grid csg-grid-four">
              <label className="cst-field"><span>{t('settings.costs.year')}</span><select className="cst-input" value={selection.year} onChange={(event) => chooseYear(event.target.value)}><option value="">{t('settings.costs.choose')}</option>{years.map((item) => <option key={item.value} value={item.value}>{item.text}</option>)}</select></label>
              <label className="cst-field"><span>{t('settings.costs.make')}</span><select className="cst-input" value={selection.make} disabled={!selection.year || catalogueBusy} onChange={(event) => chooseMake(event.target.value)}><option value="">{t('settings.costs.choose')}</option>{makes.map((item) => <option key={item.value} value={item.value}>{item.text}</option>)}</select></label>
              <label className="cst-field"><span>{t('settings.costs.model')}</span><select className="cst-input" value={selection.model} disabled={!selection.make || catalogueBusy} onChange={(event) => chooseModel(event.target.value)}><option value="">{t('settings.costs.choose')}</option>{models.map((item) => <option key={item.value} value={item.value}>{item.text}</option>)}</select></label>
              <label className="cst-field"><span>{t('settings.costs.variant')}</span><select className="cst-input" value={selection.vehicleId} disabled={!selection.model || catalogueBusy} onChange={(event) => chooseVehicle(event.target.value)}><option value="">{t('settings.costs.choose')}</option>{options.map((item) => <option key={item.value} value={item.value}>{item.text}</option>)}</select></label>
            </div>
            {catalogueBusy && <div className="csg-inline"><span className="cst-spinner" />{t('settings.costs.catalogueLoading')}</div>}
            {catalogueError && <div className="cst-alert cst-alert-warning">{catalogueError}<button type="button" onClick={() => set('mode', 'manual')}>{t('settings.costs.useManual')}</button></div>}
          </div>}

          <div className="csg-grid">
            <label className="cst-field"><span>{t('settings.costs.vehicleName')}</span><input className="cst-input" value={form.name} onChange={(event) => set('name', event.target.value)} /></label>
            <label className="cst-field"><span>{t('settings.costs.licensePlate')}</span><input className="cst-input" value={form.licensePlate} onChange={(event) => set('licensePlate', event.target.value)} /></label>
            {form.mode === 'manual' && <><label className="cst-field"><span>{t('settings.costs.year')}</span><input className="cst-input" type="number" min="1886" value={form.year} onChange={(event) => set('year', event.target.value)} /></label><label className="cst-field"><span>{t('settings.costs.make')}</span><input className="cst-input" value={form.make} onChange={(event) => set('make', event.target.value)} /></label><label className="cst-field"><span>{t('settings.costs.model')}</span><input className="cst-input" value={form.model} onChange={(event) => set('model', event.target.value)} /></label><label className="cst-field"><span>{t('settings.costs.details')}</span><input className="cst-input" value={form.details} onChange={(event) => set('details', event.target.value)} /></label></>}
            <label className="cst-field"><span>{t('settings.costs.energyType')}</span><select className="cst-input" value={form.energyType} onChange={(event) => set('energyType', event.target.value)}>{['GASOLINE', 'DIESEL', 'ELECTRIC'].map((energy) => <option key={energy} value={energy}>{t('settings.costs.energy.' + energy.toLowerCase())}</option>)}</select></label>
            <label className="cst-field"><span>{t('settings.costs.consumption')}</span><input className="cst-input" type="number" min="0.1" step="0.1" value={form.consumptionPer100Km} onChange={(event) => set('consumptionPer100Km', event.target.value)} /></label>
            <label className="cst-field"><span>{t('settings.costs.odometerUnit')}</span><select className="cst-input" value={form.odometerUnit} onChange={(event) => set('odometerUnit', event.target.value)}><option value="KM">{t('costs.unit.km')}</option><option value="MI">{t('costs.unit.mi')}</option></select></label>
            <label className="cst-field"><span>{t('settings.costs.country')}</span><select className="cst-input" value={form.fuelCountryCode} onChange={(event) => set('fuelCountryCode', event.target.value)}>{countries.map((country) => <option value={country.code} key={country.code}>{country.name || country.code} ({country.currency || form.electricityPriceCurrency})</option>)}</select></label>
            {form.energyType === 'ELECTRIC' && <><label className="cst-field"><span>{t('settings.costs.electricityPrice')}</span><input className="cst-input" type="number" min="0.01" step="0.01" value={form.electricityPricePerKwh} onChange={(event) => set('electricityPricePerKwh', event.target.value)} /></label><label className="cst-field"><span>{t('settings.costs.currency')}</span><input className="cst-input" maxLength={3} value={form.electricityPriceCurrency || selectedCountry?.currency || ''} onChange={(event) => set('electricityPriceCurrency', event.target.value.toUpperCase())} /></label></>}
          </div>
          <label className="csg-check"><input type="checkbox" checked={form.isDefault} onChange={(event) => set('isDefault', event.target.checked)} /><span><strong>{t('settings.costs.defaultVehicle')}</strong><small>{t('settings.costs.defaultVehicleHint')}</small></span></label>
          {formError && <div className="cst-alert cst-alert-error">{formError}</div>}
          <footer className="csg-actions">
            {form.id && (deleteId === form.id ? <div className="csg-delete-confirm"><span>{t('settings.costs.deleteConfirm')}</span><button type="button" className="cst-btn-ghost" onClick={() => setDeleteId('')}>{t('common.cancel')}</button><button type="button" className="csg-danger-btn" disabled={saving} onClick={() => void removeVehicle(form.id)}>{t('common.confirmDelete')}</button></div> : <button type="button" className="csg-danger-text" onClick={() => setDeleteId(form.id)}><span className="material-symbols-rounded">delete</span>{t('common.delete')}</button>)}
            <span className="csg-spacer" /><button type="button" className="cst-btn-ghost" disabled={saving || catalogueBusy} onClick={closeEditor}>{t('common.cancel')}</button><button type="button" className="cst-btn-primary" disabled={saving} onClick={() => void saveVehicle()}>{saving && <span className="cst-spinner" />}{saving ? t('common.saving') : t('common.save')}</button>
          </footer>
        </section>
      )}

      {!canWrite && !canApprove && <div className="csg-readonly"><span className="material-symbols-rounded">info</span>{t('settings.costs.organizationReadonlyHint')}</div>}
    </div>
  );
}
